import React, {useEffect, useMemo, useRef, useState} from "react";
import {AppContainer} from "../domain/AppContainer";
import {useObservable, useObservableWithInitialValue} from "../hooks/useEntityStore";
import {useHistory} from "react-router-dom";
import {ajax} from "rxjs/ajax";
import {Subscription} from "rxjs";
import {Player} from "../domain/player/models";
import {LeagueEvent} from "../domain/leagueevent/models";
import {PlayerFilterableTable} from "../components/PlayerFilterableTable";
import {LeagueEventTiles} from "../components/LeagueEventTiles";
import './LeagueDashboard.css';
import {ShareButton} from "../component-library/ShareButton";

interface Props {
    container: AppContainer;
}

const MAX_RECENT_EVENTS = 5;

function mostRecentDoneEvents(events: LeagueEvent[], limit: number): LeagueEvent[] {
    return events
        .filter(e => e.status === 'done' && e.date)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, limit);
}

function mostRecentPublicEvents(events: {id: number, name: string, dateString: string}[], limit: number): {id: number, name: string, dateString: string}[] {
    return events
        .filter(e => !!e.dateString)
        .sort((a, b) => new Date(b.dateString).getTime() - new Date(a.dateString).getTime())
        .slice(0, limit);
}

export function LeagueDashboard({container}: Props) {
    const history = useHistory();
    const active = useObservable(container.league.query.active$);
    const authed = useObservable(container.auth.query.authenticated$);
    const players = useObservableWithInitialValue(container.player.query.players$, [] as Player[]);
    const events = useObservableWithInitialValue(container.leagueEvent.query.events$, [] as LeagueEvent[]);
    const [publicEvents, setPublicEvents] = useState<LeagueEvent[]>([]);
    const publicEventsSub = useRef<Subscription | null>(null);

    useEffect(() => {
        container.leagueEvent.service.getAllForLeague();
    }, [container.leagueEvent.service, active?.id]);

    useEffect(() => {
        publicEventsSub.current?.unsubscribe();
        if (authed !== false || !active?.id) return;
        publicEventsSub.current = ajax.getJSON<{id: number, name: string, dateString: string}[]>(
            `/api/v2/leagues/${active.id}/events`
        ).subscribe({
            next: data => {
                const recent = mostRecentPublicEvents(data ?? [], MAX_RECENT_EVENTS);
                setPublicEvents(recent as unknown as LeagueEvent[]);
            },
            error: () => {}
        });
        return () => publicEventsSub.current?.unsubscribe();
    }, [authed, active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const recentEvents = useMemo(() => mostRecentDoneEvents(events, MAX_RECENT_EVENTS), [events]);

    function clickedRow(p: Player) {
        container.player.service.setActive(p.id);
        history.push(`/player/${p.id}`);
    }

    return <section className="league-dashboard">
        {active && (
            <section className="league-info">
                <address>
                    {active.address !== '' && <div>{active.address}</div>}
                    {active.website !== '' && <div><a href={active.website}>{active.website}</a></div>}
                </address>
                <ShareButton toShare={window.location.href}/>
            </section>
        )}
        {active && (authed ? recentEvents : publicEvents).length > 0 && (
            <section className="league-dashboard-recent-events">
                <h3>Recent events</h3>
                <LeagueEventTiles leagueId={active.id} events={authed ? recentEvents : publicEvents} usePublicLink={!authed}/>
            </section>
        )}
        <section className="league-dashboard-players">
            <PlayerFilterableTable
                players={players}
                storageNamespace="league-dashboard"
                defaultActivityDays={365}
                onRowClick={clickedRow}/>
        </section>
    </section>;
}
